import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { authExtended as auth } from '../dashboard/api';
import visionLiveLogo from '../assets/images/visionlive.png';
import mojenaocaleLogo from '../assets/branding/mojenaocale-logo.webp';

// InputField component - must be outside to prevent re-creation on each render
const InputField = ({ icon: Icon, type = 'text', placeholder, value, onChange, ...props }) => (
  <div className="relative">
    <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-[#111118] border border-white/10 text-white placeholder:text-white/40 focus:border-blue-500 focus:outline-none transition-colors"
      {...props}
    />
  </div>
);

// Header component matching landing page style
const Header = ({ onBack }) => {
  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 z-50 bg-[#0d0d0d]/90 backdrop-blur-md border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white/60" />
            </button>
          )}
          <a href="/" className="flex items-center gap-2">
            <img
              src={visionLiveLogo}
              alt="VisionLive"
              className="h-9 w-auto max-w-[220px] object-contain"
            />
          </a>
        </div>
        
        <div className="hidden md:flex items-center gap-8">
          <a href="/#features" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Features
          </a>
          <a href="/#partnership" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Partner Launch
          </a>
          <a href="/#faq" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            FAQ
          </a>
        </div>
      </div>
    </motion.nav>
  );
};

const AuthPage = ({ onBack, initialMode = 'login', onSuccess }) => {
  const [mode, setMode] = useState(initialMode === 'forgot' || initialMode === 'verify' ? initialMode : 'login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form fields
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (mode === 'register') {
      setMode('login');
    }
  }, [mode]);

  // Preserve legacy checkout params for private onboarding flows.
  const urlParams = new URLSearchParams(window.location.search);
  const selectedPlan = urlParams.get('plan'); // 'starter' or 'advanced'
  const billingCycle = urlParams.get('billing'); // 'monthly' or 'annual'
  const setupOnly = urlParams.get('setup') === '25'; // one-time catalog setup

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      // Store plan or setup info in sessionStorage for after OAuth redirect
      if (setupOnly) {
        sessionStorage.setItem('pendingSetup', '25');
      } else if (selectedPlan && billingCycle) {
        sessionStorage.setItem('pendingPlan', selectedPlan);
        sessionStorage.setItem('pendingBilling', billingCycle);
      }
      await auth.signInWithGoogle();
    } catch (err) {
      setError('Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await auth.forgotPassword(email);
      setSuccess('If an account exists with this email, you will receive a password reset link.');
    } catch (err) {
      // Don't reveal if email exists
      setSuccess('If an account exists with this email, you will receive a password reset link.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await auth.resendVerification(email);
      setSuccess('Verification email sent! Please check your inbox.');
    } catch (err) {
      setError('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header onBack={onBack} />
      
      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-80px)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Card */}
          <div className="bg-[#111118] rounded-2xl border border-white/10 p-8">
            {/* Plan / Setup Badge */}
            {(selectedPlan || setupOnly) && (
              <div className="flex justify-center mb-4">
                <span className="px-4 py-1.5 rounded-full bg-blue-500/20 text-blue-400 text-sm font-medium">
                  {setupOnly
                    ? 'Catalog setup – 25 products'
                    : `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan • ${billingCycle === 'annual' ? 'Annual' : 'Monthly'}`}
                </span>
              </div>
            )}

            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">
                {mode === 'login' && 'Welcome back'}
                {mode === 'forgot' && 'Reset password'}
                {mode === 'verify' && 'Verify your email'}
              </h1>
              <p className="text-white/60">
                {mode === 'login' && (selectedPlan || setupOnly ? 'Sign in to continue to checkout' : 'Sign in to access your dashboard')}
                {mode === 'forgot' && 'We\'ll send you a reset link'}
                {mode === 'verify' && 'Check your inbox for verification email'}
              </p>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 mb-6 rounded-xl bg-red-500/10 border border-red-500/20"
              >
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 mb-6 rounded-xl bg-green-500/10 border border-green-500/20"
              >
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-sm text-green-400">{success}</p>
              </motion.div>
            )}

            {/* Verification Mode */}
            {mode === 'verify' && (
              <div className="space-y-4">
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-blue-400" />
                  </div>
                  <p className="text-white/60 mb-6">
                    We've sent a verification email to <strong className="text-white">{email}</strong>
                  </p>
                  <button
                    onClick={handleResendVerification}
                    disabled={loading}
                    className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    {loading ? 'Sending...' : 'Resend verification email'}
                  </button>
                </div>
                <button
                  onClick={() => { setMode('login'); setSuccess(null); }}
                  className="w-full py-3 rounded-xl text-white/60 hover:text-white transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            )}

            {/* Login - Google Only */}
            {mode === 'login' && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-white text-gray-900 font-semibold hover:bg-gray-100 disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>

                <div className="rounded-2xl border border-[#3B82F6]/15 bg-[linear-gradient(160deg,rgba(59,130,246,0.12),rgba(17,17,24,0.96))] p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white px-2 shadow-lg shadow-black/20">
                      <img
                        src={mojenaocaleLogo}
                        alt="MojeNaočale.ba"
                        className="max-h-full w-auto object-contain"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#93C5FD]">
                        Live in production
                      </p>
                      <p className="text-sm font-medium text-white">
                        VisionLive powers mojenaocale.ba
                      </p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-white/65">
                    300+ frames, real customers, measurable conversion lift — see it live.
                  </p>
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <a
                      href="https://mojenaocale.ba"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-xl bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/15"
                    >
                      Visit mojenaocale.ba
                    </a>
                    <a
                      href="/#pricing"
                      className="inline-flex items-center justify-center rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-white/80 transition-colors hover:border-white/20 hover:text-white"
                    >
                      View pricing
                    </a>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(null); setSuccess(null); }}
                  className="w-full py-3 rounded-xl text-sm text-white/50 hover:text-white transition-colors"
                >
                  Need a password reset link?
                </button>
              </div>
            )}

            {/* Forgot Password Form */}
            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <InputField
                  icon={Mail}
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Send reset link
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(null); setSuccess(null); }}
                  className="w-full py-3 rounded-xl text-white/60 hover:text-white transition-colors"
                >
                  Back to sign in
                </button>
              </form>
            )}
          </div>
          
          {/* Footer */}
          <p className="text-center text-white/30 text-xs mt-6">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-white/50 hover:text-white hover:underline transition-colors">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-white/50 hover:text-white hover:underline transition-colors">Privacy Policy</a>.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
